<script lang="ts">
export default {
  name: 'PolicyEditHistoryPanel',
};
</script>

<script setup lang="ts">
import { DateTime } from 'luxon';
import { policyFieldLabels } from '@/components/v2/policy/policyFieldLabels';
import type { AuditLog } from '@/types/AuditLog';

defineProps<{
  auditLogs: AuditLog[];
  loading: boolean;
  recordLabel: string;
}>();

interface ParsedChange {
  changed: string;
  original: string;
}

function parseChangeDiff(raw: string): Record<string, ParsedChange> {
  try {
    return JSON.parse(raw) as Record<string, ParsedChange>;
  } catch {
    return {};
  }
}

function fieldLabel(key: string): string {
  return policyFieldLabels[key]?.label ?? key;
}

function formatValue(key: string, val: string): string {
  if (!val) return '';
  const formatter = policyFieldLabels[key]?.formatter;
  return formatter ? formatter(val) : val;
}

function formatTimestamp(iso?: string): string {
  if (!iso) return '';
  return DateTime.fromISO(iso, { zone: 'utc' }).toLocal().toLocaleString(DateTime.DATETIME_SHORT);
}
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="text-sm text-slate-medium">
      Override history for <strong class="text-slate-dark">{{ recordLabel }}</strong>
    </div>

    <div
      v-if="loading"
      class="flex h-32 items-center justify-center">
      <span class="loading loading-md loading-spinner"></span>
    </div>

    <div
      v-else-if="auditLogs.length === 0"
      class="rounded border border-base-300 p-6 text-center text-sm text-slate-medium">
      No override history found.
    </div>

    <div
      v-else
      class="flex flex-col gap-2">
      <div
        v-for="(rec, index) in auditLogs"
        :key="`${rec.createdAt ?? index}-${index}`"
        class="collapse-arrow collapse border border-base-300 bg-base-100">
        <input
          type="radio"
          :name="`history-${recordLabel}`" />
        <div class="collapse-title flex flex-col gap-0.5 px-4 py-3">
          <div class="text-sm font-medium text-slate-dark">{{ formatTimestamp(rec.createdAt) }}</div>
          <div class="text-xs text-slate-medium">{{ rec.userEmail }}</div>
        </div>
        <div class="collapse-content px-4 pb-3">
          <table class="w-full table-fixed">
            <thead>
              <tr class="border-b border-base-300 bg-base-200 text-left text-xs tracking-wide text-slate-medium uppercase">
                <th class="w-2/5 px-3 py-2 font-semibold">Field</th>
                <th class="w-1/4 px-3 py-2 font-semibold">Original</th>
                <th class="px-3 py-2 font-semibold">Changed</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(diff, key) in parseChangeDiff(rec.changeDiff)"
                :key="key"
                class="border-b border-base-200 text-sm last:border-b-0">
                <td class="px-3 py-2 text-slate-dark">{{ fieldLabel(key) }}</td>
                <td class="px-3 py-2">
                  <span
                    v-if="diff.original"
                    class="text-error"
                    >{{ formatValue(key, diff.original) }}</span
                  >
                  <span
                    v-else
                    class="text-xs text-slate-light italic"
                    >—</span
                  >
                </td>
                <td class="px-3 py-2">
                  <span
                    v-if="diff.changed"
                    class="font-medium text-success"
                    >{{ formatValue(key, diff.changed) }}</span
                  >
                  <span
                    v-else
                    class="text-xs text-slate-light italic"
                    >—</span
                  >
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>
